'use client';

import { formatDateTime } from '@pickleballinc/lib/format-dates';
import { getCDNURL } from '@pickleballinc/lib/get-cdn-url';
import { TEAM_LEAGUES_PLACEHOLDER_IMAGE } from '@pickleballinc/ui/components/global/utils/constants';
import { useMemo, useState } from 'react';

import { ModuleCard } from './ModuleCard';
import { ModuleCardBody } from './ModuleCardBody';

export type UseFavoriteState = (
	id: string,
	initialIsFavorite?: boolean
) => {
	isFavorite: boolean;
	isFavoriteDisabled: boolean;
	isUserLoggedIn: boolean;
	onToggle: (event: React.MouseEvent, id?: string, isFavourite?: boolean | null) => void;
};

const useDefaultFavoriteState: UseFavoriteState = () => ({
	isFavorite: false,
	isFavoriteDisabled: true,
	isUserLoggedIn: false,
	onToggle: () => {}
});

export interface TeamLeagueCardProps {
	id: string;
	slug: string;
	title: string;
	logo: string;
	startDatetime: string;
	endDatetime: string;
	location?: string;
	playerCount: number;
	hidePlayersCount?: boolean;
	isSeasonActive?: boolean;
	seasonStandingsExist?: boolean;
	teamLeaguesBaseUrl: string;
	customCardHref?: string;
	/**
	 * Render the click-through as a plain `<a>` (full document navigation) instead of a Next `<Link>`.
	 * Set by the listing-ad rotator: an ad's URL carries Everflow parameters that the landing middleware
	 * has to see, and Next would route a same-origin URL client-side and skip it.
	 */
	nativeAnchor?: boolean;
	hideCardHref?: boolean;
	className?: string;
	logoClassName?: string;
	showFavoriteIcon?: boolean;
	useFavoriteState?: UseFavoriteState;
	initialIsFavorite?: boolean;
	variant?: 'light' | 'dark';
}

export const TeamLeagueCardv2 = (props: TeamLeagueCardProps) => {
	const { id, slug, customCardHref, teamLeaguesBaseUrl, hideCardHref = false, isSeasonActive, seasonStandingsExist } = props;

	const href = hideCardHref
		? undefined
		: customCardHref
			? customCardHref
			: `${teamLeaguesBaseUrl}/team-leagues/${slug ? slug : id}${isSeasonActive && seasonStandingsExist ? '/standings' : ''}`;

	return <TeamLeagueCardComponent {...props} href={href} />;
};

const TeamLeagueCardComponent = ({
	id,
	title,
	logo,
	startDatetime,
	endDatetime,
	location,
	playerCount,
	hidePlayersCount,
	className,
	logoClassName,
	href,
	nativeAnchor,
	showFavoriteIcon,
	variant = 'light',
	useFavoriteState,
	initialIsFavorite
}: TeamLeagueCardProps & { href?: string }) => {
	const [isPlaceholderImage, setIsPlaceholderImage] = useState(false);

	const getImageToDisplay = useMemo(() => {
		if (logo) {
			return getCDNURL(logo, 300, 150);
		}
		setIsPlaceholderImage(true);
		return getCDNURL(TEAM_LEAGUES_PLACEHOLDER_IMAGE, 300, 150);
	}, [logo]);

	const images = [getImageToDisplay];

	const formattedStartDate = startDatetime ? formatDateTime(startDatetime, { parseZone: true }) : null;
	const formattedEndDate = endDatetime ? formatDateTime(endDatetime, { parseZone: true }) : null;

	const dateRangeContent =
		formattedStartDate && formattedEndDate
			? `${formattedStartDate} - ${formattedEndDate}`
			: formattedEndDate
				? `Ended on ${formattedEndDate}`
				: null;

	const metaLines = [{ id: 'date-range', content: dateRangeContent }];

	const playersLabel = !hidePlayersCount ? `${playerCount} player${playerCount !== 1 ? 's' : ''}` : null;

	const showFooterRow = Boolean(playersLabel);

	const { isFavorite, isFavoriteDisabled, isUserLoggedIn, onToggle } = (useFavoriteState ?? useDefaultFavoriteState)(id, initialIsFavorite);

	return (
		<div className="flex flex-col h-full gap-2">
			<div className="relative min-[500px]:min-w-[224px] min-[500px]:max-w-[286px]">
				<ModuleCard
					id={id}
					images={images}
					imageAlt={title}
					isPlaceholderImage={isPlaceholderImage}
					href={href}
					nativeAnchor={nativeAnchor}
					className={className}
					imageContainerClassName={logoClassName}
					imageClassName={logoClassName}
					showFavoriteIcon={showFavoriteIcon}
					isUserLoggedIn={isUserLoggedIn}
					isFavourite={isFavorite}
					isFavoriteDisabled={isFavoriteDisabled}
					onFavoriteToggle={onToggle}
				>
					<ModuleCardBody
						title={title}
						subtitle={location}
						metaLines={metaLines}
						footerLeft={showFooterRow ? playersLabel : null}
						footerRight={null}
						variant={variant}
					/>
				</ModuleCard>
			</div>
		</div>
	);
};
